import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AndroidBridge.js
 *
 * Handles token management ONLY.
 * Permission explainers have been moved to Onboarding.js and Settings.js.
 *
 * Token strategy:
 *   1. On sign-in, push Clerk JWT to native immediately via setToken().
 *   2. Then mint a 7-day Qude detection JWT and push that on top.
 *   3. Refresh every 30 minutes and on foreground.
 */

const TOKEN_REFRESH_MS = 30 * 60 * 1000;
const MINT_TIMEOUT_MS = 5000;
const LAUNCH_RELAY_POLL_MS = 2000;

export default function AndroidBridge() {
  const { isSignedIn, getToken } = useAuth();
  const refreshTimerRef = useRef(null);
  const launchPollTimerRef = useRef(null);
  const launchPollBusyRef = useRef(false);

  const pushTokenToNative = (token, label) => {
    if (typeof window === 'undefined') return;
    if (!window.QudeAndroid?.setToken) return;
    if (!token) return;
    try {
      window.QudeAndroid.setToken(token);
      console.log(`[Qude] ${label}: pushed to Android (len=${token.length})`);
    } catch (e) {
      console.error(`[Qude] ${label}: setToken threw`, e);
    }
  };

  const mintQudeToken = async (clerkToken, label) => {
    if (!clerkToken) return null;
    try {
      const res = await axios.post(
        `${API}/auth/detection-token`,
        {},
        {
          headers: { Authorization: `Bearer ${clerkToken}` },
          timeout: MINT_TIMEOUT_MS,
        },
      );
      const token = res?.data?.token;
      if (!token) return null;
      console.log(`[Qude] mint (${label}): got Qude token (expires ${res.data.expires_at})`);
      return token;
    } catch (e) {
      console.warn(`[Qude] mint (${label}): failed —`, e?.message || e);
      return null;
    }
  };

  const refreshNativeToken = async (label) => {
    if (typeof window === 'undefined' || !window.QudeAndroid?.setToken) return;
    let clerkToken = null;
    try {
      clerkToken = await getToken({ skipCache: true });
    } catch (e) {
      console.error(`[Qude] ${label}: getToken(skipCache) failed`, e);
    }
    const qudeToken = await mintQudeToken(clerkToken, label);
    if (qudeToken) {
      pushTokenToNative(qudeToken, `${label}-qude`);
      return;
    }
    pushTokenToNative(clerkToken, `${label}-clerk-fallback`);
  };

  // ── Sign-in / sign-out lifecycle ───────────────────────────────────────

  useEffect(() => {
    if (typeof window === 'undefined' || !window.QudeAndroid) return;

    if (isSignedIn) {
      getToken()
        .then(async (clerkToken) => {
          if (!clerkToken) return;
          // Push Clerk immediately — guarantees service starts
          pushTokenToNative(clerkToken, 'sign-in-clerk');
          // Then upgrade to 7-day Qude token
          const qudeToken = await mintQudeToken(clerkToken, 'sign-in');
          if (qudeToken) {
            pushTokenToNative(qudeToken, 'sign-in-qude-upgrade');
          }
        })
        .catch((e) => console.error('[Qude] sign-in: getToken error:', e));
    } else {
      if (window.QudeAndroid?.stopDetection) {
        window.QudeAndroid.stopDetection();
        console.log('[Qude] Detection stopped');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, getToken]);

  // ── Token refresh timer + foreground refresh ───────────────────────────

  useEffect(() => {
    if (!isSignedIn) {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    refreshTimerRef.current = setInterval(() => {
      refreshNativeToken('30min-tick');
    }, TOKEN_REFRESH_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshNativeToken('foreground');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  // ── Launch relay: foreground poll fallback ─────────────────────────────
  // The Roku channel can't ECP-launch itself (Roku policy), so it asks the
  // backend to relay the launch to this phone, which CAN ECP-launch it (same
  // path the Remote page's Open App grid already uses). A native OneSignal
  // push covers the backgrounded case; this poll is the foreground fallback
  // (also the ONLY path today if push isn't configured/reachable). It's a
  // no-op unless window.QudeAndroid exists, and setInterval is naturally
  // paused by MainActivity's webView.onPause() while backgrounded, so this
  // never runs when the app isn't in front.
  const pollLaunchRequest = async () => {
    if (typeof window === 'undefined' || !window.QudeAndroid?.launchRokuAppWithContent) return;
    if (document.visibilityState !== 'visible') return;
    if (launchPollBusyRef.current) return;
    launchPollBusyRef.current = true;
    try {
      const token = await getToken();
      if (!token) return;
      const res = await axios.get(`${API}/qude-protocol/launch-request/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const req = res?.data?.request;
      if (!req) return;

      // Don't just grab the first discovered Roku — a household with more
      // than one Roku-based TV would silently send the launch to the wrong
      // screen. resolveQudeRokuIp checks each candidate for the actual
      // sideloaded Qude channel and returns the one that has it.
      let ip = '';
      try {
        ip = window.QudeAndroid.resolveQudeRokuIp?.() || '';
      } catch { /* no-op — ip stays empty, reported as failure below */ }

      let success = false;
      let httpCode = 0;
      if (ip && req.roku_channel_id) {
        const result = window.QudeAndroid.launchRokuAppWithContent(
          ip, req.roku_channel_id, req.content_id || '', req.media_type || '',
        );
        success = result === 'ok';
        httpCode = parseInt(String(result).replace('error:', ''), 10) || 0;
      }

      await axios.post(
        `${API}/qude-protocol/launch-request/${req.request_id}/complete`,
        { success, http_code: httpCode },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // Silent — this is a background poll, not a user-initiated action.
    } finally {
      launchPollBusyRef.current = false;
    }
  };

  useEffect(() => {
    if (!isSignedIn || typeof window === 'undefined' || !window.QudeAndroid) return;

    launchPollTimerRef.current = setInterval(pollLaunchRequest, LAUNCH_RELAY_POLL_MS);

    return () => {
      if (launchPollTimerRef.current) {
        clearInterval(launchPollTimerRef.current);
        launchPollTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  return null;
}
